<script setup lang="ts">
/**
 * The AI 助手 in the navbar, right of the search box, when the admin
 * offers it there. With its name (the admin's choice, and room for it): a
 * cell the bar's full height like the text links, the mark and the name.
 * Without: a bare icon button like the theme's and 页面配置's, the name on
 * hover. Opens the panel under the navbar.
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { assistant, assistantName, offered, toggleAssistant } from '@/utils/assistant'
import { isNarrow } from '@/utils/responsive'
import AssistantMark from './AssistantMark.vue'

const { t, locale } = useI18n()
const info = computed(() => assistant.info)
const name = computed(() => assistantName(info.value, locale.value, t('assistant.defaultName')))
const open = computed(() => assistant.open && assistant.from === 'navbar')
const labelled = computed(() => info.value?.nav_label !== false && !isNarrow.value)

// The bare button's name on hover, held in hand: gone with the click that
// opens the panel, and not shown while it is open.
const tip = ref(false)
function toggle() {
  tip.value = false
  toggleAssistant('navbar')
}
</script>

<template>
  <template v-if="info && offered('navbar')">
    <button
      v-if="labelled"
      type="button"
      class="nav-link assistant-nav"
      :class="{ open }"
      :aria-label="name"
      :aria-expanded="open"
      @click="toggleAssistant('navbar')"
    >
      <AssistantMark :icon="info.icon" :animated="info.animated" :size="20" />
      <span class="label">{{ name }}</span>
    </button>
    <a-tooltip v-else :content="name" :popup-visible="tip && !open" @popup-visible-change="(v: boolean) => (tip = v)">
      <a-button
        class="nav-btn assistant-icon"
        :class="{ open }"
        type="text"
        shape="square"
        :aria-label="name"
        :aria-expanded="open"
        @click="toggle"
      >
        <template #icon><AssistantMark :icon="info.icon" :animated="info.animated" :size="20" /></template>
      </a-button>
    </a-tooltip>
  </template>
</template>

<style scoped>
.assistant-nav {
  gap: 6px;
}
.label {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* The bare button keeps its hover grey while its panel is out, as the
   text links' cells do (.nav-link.open). */
.assistant-icon.open {
  background-color: var(--color-fill-2);
}
.assistant-icon :deep(.arco-btn-icon) {
  display: inline-flex;
}
</style>
